import { AlertTriangle, PlusCircle } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import type { RenameTarget } from '@/lib/items-api';

/**
 * ZAKRES ZMIANY KLUCZA — ile wierszy i w których tabelach.
 *
 * ⚠️ ISTNIEJE PRZEZ DWA ROZSTRZYGNIĘCIA, A NIE DLA ŁADNEJ LISTY (`PLAN.md` §8b, D1 i D2):
 *
 * • `collateral` — wiersze, których operacja NIE DOTYCZY, a które kaskada SPS przepisze razem
 *   z resztą, bo w tym miejscu nie ma dyskryminatora. Zgodziliśmy się na parytet z SPS pod
 *   warunkiem, że te wiersze widać PRZED kliknięciem: klient może wykonać tę samą zmianę ze
 *   SPSConfiga, więc dwie różne semantyki byłyby gorsze niż jedna niedoskonała, ale jawna.
 * • `beyondSps` — kroki, których kaskada SPS NIE MA. Bez nich prefiksy podgrup zostają wskazujące
 *   kod, którego już nie ma, czyli cicha utrata reguł dostępu. Robimy je, ale mówimy o tym.
 *
 * Tabele z zerem wierszy pomijamy: „ExternalActInv: 0" to szum, a lista ma się dać przeczytać.
 */
export function PodgladKaskady({ targets }: { targets: readonly RenameTarget[] }) {
  const { t } = useI18n();
  const widoczne = targets.filter((x) => x.rows > 0);
  if (widoczne.length === 0) return null;

  const suma = widoczne.reduce((a, x) => a + x.rows, 0);
  const collateral = widoczne.filter((x) => x.collateral).reduce((a, x) => a + x.rows, 0);
  const ponad = widoczne.filter((x) => x.beyondSps).reduce((a, x) => a + x.rows, 0);

  return (
    <div className="rounded-md border border-border text-xs">
      <div className="flex items-baseline justify-between border-b border-border px-3 py-2">
        <span className="font-medium">{t('klucz.zakres')}</span>
        <span className="text-muted-foreground">{t('klucz.zakresSuma').replace('{n}', String(suma))}</span>
      </div>

      <ul className="max-h-56 divide-y divide-border overflow-y-auto">
        {widoczne.map((x) => (
          <li key={`${x.table}.${x.column}`} className="flex items-start gap-2 px-3 py-1.5">
            {x.collateral && <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-[var(--warning)]" />}
            {x.beyondSps && <PlusCircle className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />}
            <span className="min-w-0 flex-1">
              <span className="block font-mono text-[11px] leading-snug">
                {x.table}.{x.column}
              </span>
              <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">{x.note}</span>
            </span>
            <span className="shrink-0 tabular-nums text-muted-foreground">{x.rows}</span>
          </li>
        ))}
      </ul>

      {(collateral > 0 || ponad > 0) && (
        <div className="space-y-1 border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
          {collateral > 0 && (
            <p className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 size-3 shrink-0 text-[var(--warning)]" />
              <span>{t('klucz.przyOkazji').replace('{n}', String(collateral))}</span>
            </p>
          )}
          {ponad > 0 && (
            <p className="flex items-start gap-1.5">
              <PlusCircle className="mt-0.5 size-3 shrink-0" />
              <span>{t('klucz.ponadSps').replace('{n}', String(ponad))}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
